import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Box,
} from "@mui/material";
import { http } from "../../../../../clients/http/http.client";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import StatusCard from "../../../../Inputs/StatusCard/StatusCard";
import { HTTP_ENDPOINTS } from "../../../../../clients/clients.constants";
import Loader from "../../../../Inputs/Loader/Loader";
import {detectStatusChange} from "../../../../../store/updateAlertStatusActionSlice";
import { formatDateTimeInTimeZone } from "../../../../../helpers/useTimeZoneTime";
import {TableVirtuoso} from "react-virtuoso";
import Paper from "@mui/material/Paper";

const { ALERTS, UPDATE_ALERT_STATUS } = HTTP_ENDPOINTS;

const defaultPage = 1;

export default function AlertsTable({ sort, search, isDebounceRunning }) {
  const [expandedRows, setExpandedRows] = React.useState({});
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("");
  const [isScrollLoading, setIsScrollLoading] = useState(false);
  const [limit, setLimit] = useState(50);
  const [isEndReached, setIsEndReached] = useState(false);
  const [page, setPage] = useState(defaultPage);
  const [rowData, setRowData] = useState([]); // State to store data
  const [loading, setLoading] = useState(true); // To show loading state
  const [error, setError] = useState(null); // To handle any errors
  const dispatch = useDispatch();
  const alertsDateFilters = useSelector((state) => state.actionsDateFilters);
  const filters = useSelector((state) => state.alertsFiltersOptions);
  const timeZone = useSelector((state) => state.timeZone.timeZone); // Access timeZone from Redux
  const siteIdStore = useSelector((state) => state.siteId);

  const buildAlertsTableQueryParams = () => {
    let params = new URLSearchParams();

    // Date filters
    if (alertsDateFilters?.from && alertsDateFilters?.to) {
      const fromDate = DateTime.fromISO(alertsDateFilters.from).toUTC();
      const toDate = DateTime.fromISO(alertsDateFilters.to).toUTC();

      // if (fromDate.isValid && toDate.isValid) {
      //   params.append('from', fromDate.toISO());
      //   params.append('to', toDate.toISO());
      // }
    }

    if (filters?.brand?.selected?.length > 0) {
      params.append('brand', filters.brand.selected.join(','));
    }

    if (filters?.siteType?.selected?.length > 0) {
      params.append('siteType', filters.siteType.selected.join(','));
    }

    if (filters?.region?.selected?.length > 0) {
      params.append('region', filters.region.selected.join(','));
    }

    if (filters?.countryState?.selected?.length > 0) {
      params.append('countryState', filters.countryState.selected.join(','));
    }

    if (siteIdStore?.siteId) {
      params.append('siteId', siteIdStore.siteId);
    }

    if (sort) {
      params.append('sortField', sort || '');
    }

    if (search) {
      params.append('search', search || '');
    }

    params.append('page', page);
    params.append('limit', limit);

    return params.toString() ? `?${params.toString()}` : '';
  };

  const fetchDataPaginated = async () => {
    try {
      setIsScrollLoading(true);
      let queryParams = buildAlertsTableQueryParams();
      const res = await http.client.get(`${ALERTS}${queryParams}`);
      setRowData((prevState) => ({
        ...prevState,
        data: [...prevState?.data, ...res.data.data],
      }));
      setIsEndReached(res.data.data.length === 0);
      setIsScrollLoading(false);
    } catch (error) {
      setIsScrollLoading(false);
      console.log('error', error);
    }
  }

  useEffect(() => {
    let queryParams = buildAlertsTableQueryParams();

    setLoading(true);
    http.client
      .get(`${ALERTS}${queryParams}`)
      .then((response) => {

        setRowData(response.data);
        setLoading(false);
      })
      .catch((error) => {
        setError(error.message);
        setLoading(false);
      });
  }, [alertsDateFilters, filters, search, sort, siteIdStore]);

  useEffect(() => {
    if (page !== defaultPage && !isScrollLoading) {
      fetchDataPaginated();
    }
  }, [page]);

  const handleExpandClick = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const tableStyles = {
    "& .MuiTable-root": {
      borderCollapse: "separate",
      borderSpacing: 0,
      // border: "none",
    },
    "& .MuiTableCell-root": {
      fontSize: "12px",
      color: "#2D3748",
      fontWeight: 400,
      padding: "8px",
      borderBottom: "1px solid #e0e0e0!important",
    },
    //
    // "& .MuiTableRow-root": {
    //   borderBottom: "1px solid #e0e0e0!important",
    //
    // },
    "& .MuiTableHead-root": {
      borderBottom: "2px solid #e0e0e0!important", // Ensure border is applied
      backgroundColor: "#f9f9f9",
    },
    "& .MuiTableCell-head:last-child": {
      width: '100%'
    },
    "& .MuiTableCell-head": {
      borderBottom: "2px solid #e0e0e0!important", // Add border to table headers
      fontSize: "12px",
      color: "#718096",
      fontWeight: 500,
      textTransform: "uppercase",
    },

    "& .MuiIconButton-root": {
      padding: "0",
    },
    "& .MuiCollapse-root": {
      paddingLeft: "50px",
      backgroundColor: "#F9FBFF",
    },
  };

  const nestedTableStyles = {
    "& .MuiPaper-root": {
      boxShadow: "none",
    },
    "& .MuiTableHead-root": {
      backgroundColor: "#F9FBFF",
    },
    "& .MuiTableRow-root": {
      backgroundColor: "#F9FBFF!important",
      padding: "0",
    },
    "& .MuiTableCell-root": {
      fontSize: "12px",
      color: "#2D3748",
      fontWeight: 400,
      backgroundColor: "#F9FBFF",
      border: "none",
    },
    "& .MuiTableCell-head": {
      fontSize: "12px",
      color: "#718096",
      fontWeight: 500,
      textTransform: "capitalize",
    },
  };

  const NestedTable = React.memo(({ row }) => {
    return (
      <Box sx={nestedTableStyles}>
        <Table size="small" aria-label="details">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "18%" }}>Site</TableCell>
              <TableCell sx={{ width: "15%" }}>
                Quantities
              </TableCell>
              <TableCell sx={{ width: "15%" }}>
                Location
              </TableCell>
              <TableCell sx={{ width: "15%" }}>

              </TableCell>
              <TableCell sx={{ width: "15%" }}>

              </TableCell>
              <TableCell sx={{ width: "20%" }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {row?.alerts.map((detail, index) => (
              <TableRow key={index}>
                <TableCell>{detail.Site_Desc}</TableCell>
                <TableCell>{detail.ES_Quantity}</TableCell>
                <TableCell>{detail.LogLoc_Desc}</TableCell>
                <TableCell>{detail.Old_Location_Desc}</TableCell>
                <TableCell>{detail.New_Location_Desc}</TableCell>
                <TableCell><StatusCard status={detail.status} type="tableAlertCard" handleStatusCardClick={async () => await handleAlertStatusChange(detail)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  })

  const getDateTimeString = (isoDate, isoTime) => {
    if (!isoDate || !isoTime) return '';
    const datePart = isoDate.split('T')[0];
    const timePart = isoTime.split('T')[1];
    return `${datePart}T${timePart}`;
  };

  const handleAlertStatusChange = async (row) => {
    const oldStatus = row.status;
    let status;

    try {

      if (row.status === 'open') {
        status = 'inProgress'
      } else if (row.status === 'inProgress') {
        status = 'close'
      } else {
        status = 'open'
      }

      setRowData((prevState) => ({
        ...prevState,
        data: prevState.data.map((item) => ({
          ...item,
          alerts: item.alerts.map((alert) => {
            if (
              row.Site_Key === alert.Site_Key &&
              row.ET_key === alert.ET_key &&
              row.LogLoc_Key === alert.LogLoc_Key &&
              row.id === alert.id
            ) {
              return {
                ...alert,
                status
              };
            }
            return alert;
          })
        }))
      }));

      const res = await http.client.put(UPDATE_ALERT_STATUS, {
        id: row.id,
        siteKey: row.Site_Key,
        etKey: row.ET_key,
        logLocKey: row.LogLoc_Key,
        status
      });

      dispatch(detectStatusChange())
    } catch (error) {
      setRowData((prevState) => ({
        ...prevState,
        data: prevState.data.map((alert) => {
          if (row.Site_Key === alert.Site_Key && row.ET_key === alert.ET_key && row.LogLoc_Key === alert.LogLoc_Key) {
            return {
              ...alert,
              status: oldStatus
            }
          } else {
            return alert
          }
        })
      }));
      console.error(error);
    }
  }

  const columns = [
    { label: "", dataKey: "expand", width: '5%' },
    // { label: "Alert ID", dataKey: "id", width: 200 },
    { label: "Equipment Type", dataKey: "ET_Desc", width: 190 },
    { label: "Quantity", dataKey: "ES_Quantity", width: 100 },
    { label: "Description", dataKey: "LogLoc_Desc", width: 150 },
    { label: "Received On", dataKey: "receivedOn", width: 150 },
    { label: "Status", dataKey: "status", width: 100 },
  ];

  const VirtuosoTableComponents = {
    Scroller: React.forwardRef((props, ref) => (
      <TableContainer component={Paper} {...props} ref={ref} />
    )),
    Table: (props) => (
      <Table {...props} sx={{ borderCollapse: "separate", tableLayout: "fixed" }} />
    ),
    TableHead: React.forwardRef((props, ref) => (
      <TableHead {...props} ref={ref} />
    )),
    TableBody: React.forwardRef((props, ref) => (
      <TableBody {...props} ref={ref} />
    )),
    TableRow: React.forwardRef((props, ref) => {
      return (
        <>
          <TableRow {...props} ref={ref} />
          <TableRow>
            <TableCell colSpan={7} style={{ padding: 0 }}>
              <Collapse
                in={expandedRows[props?.['data-index']]}
                timeout="auto"
                unmountOnExit
              >
                <NestedTable row={props.item} />
              </Collapse>
            </TableCell>
          </TableRow>
        </>
      )
    }),
  };

  const fixedHeaderContent = (test) => {
    return (
      <TableRow sx={{ width: '100%!important' }}>
        {columns.map((column, index) => (
          <TableCell
            key={column.dataKey}
            variant="head"
            align={column.numeric ? "right" : "left"}
            style={{
              width: index + 1 === columns.length ? '100%!important' : column.width, // Last column takes the remaining space
            }}
            sx={{
              textTransform: "uppercase",
              fontSize: "14px",
              fontWeight: "500",
              color: "#718096",
              background: "#fff",
              padding: "8px 10px",
            }}
          >
            {column.label}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  function rowContent(index, row) {
    return (
      <>
        {columns.map((column) => (
          <TableCell
            key={column.dataKey}
            align={column.numeric ? "right" : "left"}
            sx={{
              fontSize: "12px",
              fontWeight: "500",
              color: "#2D3748",
              padding: "8px 10px",
            }}
          >
            {(() => {
              if (column.dataKey === 'expand') {
                return (
                  <IconButton onClick={() => handleExpandClick(index)}>
                    {expandedRows[index] ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                )
              }

              if (column.dataKey === 'receivedOn') {
                return formatDateTimeInTimeZone (
                  getDateTimeString(row.ES_Date, row.ES_Time),
                  timeZone
                );
              }

              if (column.dataKey === 'status') {
                return `${row?.alerts.filter(item => item?.status === "open")?.length} Unresolved`;
              }

              return row[column.dataKey];
            })()}
          </TableCell>
        ))}
      </>
    );
  }

  if (error) return <div>Error: {error}</div>;
  return (
    <Box sx={tableStyles}>
      <Paper style={{ height: '325px', width: '100%', boxShadow: 'none' }}>
        {
          loading || isDebounceRunning
            ? <Loader sx={{
              height: '337px'
            }} />
            : <TableVirtuoso
              data={rowData.data}
              components={VirtuosoTableComponents}
              fixedHeaderContent={fixedHeaderContent}
              itemContent={rowContent}
              endReached={() => {
                const isScrollable = rowData.data.length > 8;

                if (isScrollable && !isScrollLoading && !isEndReached) {
                  setPage((prevState) => prevState + 1);
                }
              }}
              totalCount={rowData.data.length}
              followOutput={!isEndReached}
            />
        }
      </Paper>
    </Box>
  );
}
